import { useCurrentMedication } from '@/hooks/useCurrentMedication'
import { useGoNextMedRequest } from '@/hooks/useGoNextMedRequest'
import { resetMedicationRequestStore, useMedicationRequestStore } from '@/lib/stores/medication-request'
import { mpsApprovalRowsOf, mpsApprovalWaitReasonOf, runMedRequestActionChain } from './mps-family-actions'
import { multiProductOrderApprovePlan } from './multi-product-order/approve'
import { useSyncMpsTreatmentsOnApprove } from './subscription'
import { type ActionPlan, changeProductFor } from '@/lib/med-requests'
import useMedRequestAction, { invalidateRelevantActionQueries, type MedRequestMutationFnProps } from './action'
import { useToast } from '@/hooks/use-toast'
import useBatchApproval from '@/hooks/useBatchApproval'
import { usePosthogCustom } from '@/hooks/usePosthogCustom'
import {
  checkMedicationDirectionsLength,
  convertDateToISOWithoutConverting,
  isFutureIssueDate,
  isLinkMedRequest,
  isMpsMedRequest,
  isMultiProductOrderMedRequest
} from '@/lib/utils'
import { useScrAiSummary } from '@/hooks/scr-ai-summary'
import type { MedRequest } from '@/types/data'

export type ApprovePlan = ActionPlan & {
  /** Skips the batch approval screen. */
  isForFuture: boolean
}

export default function useApproveMedicationRequest(props?: {
  patientOrigin?: 'medication-request' | 'investigation'
}) {
  const { patientOrigin = 'medication-request' } = props ?? {}

  const { medication, medicationIssueDate } = useMedicationRequestStore()
  const goNextMedRequest = useGoNextMedRequest(patientOrigin === 'medication-request')

  const medRequest = useCurrentMedication(patientOrigin === 'medication-request')
  const originalMedRequest = medRequest?.data

  const { data: batchApprovals = [], shouldUseBatchApprovalLogic } = useBatchApproval({
    medRequest: originalMedRequest
  })

  const { trackMedRequestAction } = usePosthogCustom()

  // The chain below is awaited and may span several requests; per-mutation store resets would
  // blank the open page mid-flight, so the store is reset once, right before navigating.
  const action = useMedRequestAction({
    enabled: !!(patientOrigin === 'medication-request'),
    doNotResetStore: true
  })

  const { prompt: scrExecutedPrompt, summary: scrSummaryPrompt } = useScrAiSummary({
    medicationRequestId: originalMedRequest?.id ?? ''
  })

  const syncMpsTreatments = useSyncMpsTreatmentsOnApprove()
  const { toast } = useToast()

  // Sends the plan and moves on; false when a failure kept the page for a retry.
  const send = async (medRequest: MedRequest, { actions, isChain, isForFuture }: ApprovePlan): Promise<boolean> => {
    if (!actions.length) return false

    if (!isChain) {
      action.mutate(actions[0])
      trackMedRequestAction('approve', medRequest.id)
    } else {
      // Awaited: navigation only happens once the whole family is actioned, so a mid-chain
      // failure leaves the prescriber on the request it concerns, with their edits intact.
      const chain = await runMedRequestActionChain(action.mutateAsync, actions)

      if (chain.completed > 0) trackMedRequestAction('approve', medRequest.id)

      if (!chain.ok) {
        // Refresh the caches the chain diverged from: the queue row was optimistically removed,
        // and the family's stages decide what a retry still has to send.
        invalidateRelevantActionQueries(medRequest.patient.id ?? '', medRequest.id)
        if (chain.total > 1) {
          toast({
            title: 'Approval incomplete',
            description: `${chain.completed} of ${chain.total} treatments were approved; the rest are still pending. Please try again.`
          })
        }
        return false
      }
    }

    // Move to batch approval screen if not approved for future.
    const { setStatus, setBatchOrders } = useMedicationRequestStore.getState()
    if (shouldUseBatchApprovalLogic && !isForFuture) {
      setBatchOrders(batchApprovals)
      setStatus('approved')
    } else {
      resetMedicationRequestStore()
      goNextMedRequest()
    }
    return true
  }

  // Resolves once every approve of the family has been sent — false when any step failed and
  // the page was kept for a retry, so callers hold their own cleanup (sidebar close, cleared
  // justification) until then.
  const submit = async (): Promise<boolean> => {
    if (!originalMedRequest) return false

    // Captured before any await, so nothing that refetches mid-flight can skew the payloads.
    const {
      notes,
      medicationDirection,
      extraTreatments,
      removedTreatments,
      droppedProductHashes,
      multiProductOrderTreatments,
      isTemporaryReplacement,
      isLastSubscriptionOrder,
      justification
    } = useMedicationRequestStore.getState()

    if (isMultiProductOrderMedRequest(originalMedRequest)) {
      const plan = multiProductOrderApprovePlan(originalMedRequest, {
        opened: {
          medication,
          medicationDirection,
          medicationIssueDate,
          notes,
          scrExecutedPrompt,
          scrSummaryResult: scrSummaryPrompt
        },
        treatments: multiProductOrderTreatments,
        reason: justification.trim()
      })
      return plan ? send(originalMedRequest, plan) : false
    }

    // Checked before the MPS sync, so the API can't refuse it after the subscription changed.
    if (!checkMedicationDirectionsLength(medicationDirection)) return false

    // Only mps_med_order rows reach the frontend; any family member runs the sync and the
    // family fan-out. Subscription mutations key on mpsHash; /update calls use the rows' own ids.
    const isMpsSubscription = isMpsMedRequest(originalMedRequest)

    let familyActions: MedRequestMutationFnProps[] = []

    // Treatment changes stay local until this moment: approving an MPS request first applies
    // the accumulated diffs (queued removals, adds, swaps) to the subscription, and the approve
    // action only fires once they all landed. changeProduct stays suppressed for MPS — the
    // multiproduct swap above already covers Treatment 1.
    if (isMpsSubscription) {
      const waitReason = mpsApprovalWaitReasonOf(originalMedRequest, {
        extraTreatments,
        removedTreatments,
        droppedProductHashes
      })
      if (waitReason) {
        toast({ title: 'Approval not sent', description: waitReason })
        return false
      }

      const sync = await syncMpsTreatments(originalMedRequest)

      if (!sync.ok) {
        // The only toast left standing (TOAST_LIMIT is 1), so it carries the reason the sync stopped.
        toast({
          title: 'Approval not sent',
          description: sync.error
            ? `The treatment changes could not be applied to the subscription: ${sync.error}`
            : 'The treatment changes could not be applied to the subscription. Please try again.'
        })
        return false
      }

      // Read the family again after the sync: PHP sends an added medication, or a swap onto a new
      // line, through the automated checks unless its order awaits a decision, and only this read
      // knows a new line's id. Joins the refetch the sync started, if any.
      const notSentTitle = sync.hasSavedChanges ? 'Treatment changes saved, approval not sent' : 'Approval not sent'
      const { data: refetchedMedRequest, isError } = await medRequest.refetch({ cancelRefetch: false })

      if (isError || !refetchedMedRequest) {
        toast({ title: notSentTitle, description: 'The request could not be reloaded. Please try again.' })
        return false
      }

      const picksAfterSync = useMedicationRequestStore.getState()
      const waitReasonAfterSync = mpsApprovalWaitReasonOf(refetchedMedRequest, picksAfterSync)
      if (waitReasonAfterSync) {
        toast({ title: notSentTitle, description: waitReasonAfterSync })
        return false
      }

      // Every medication of the family is a med request of its own, in clinical review
      // alongside this one — approving only the opened request would leave the siblings
      // pending in the queue. Each still-pending row of the read above gets its own approve,
      // carrying its treatment card's direction and issue date.
      const treatmentOf = (row: MedRequest): { direction: string; issueDate: Date } | null => {
        const rowHash = row.orderedItem?.id
        if (!rowHash) return null
        if ([medication?.productHash, medication?.variantHash].includes(rowHash)) {
          return { direction: medicationDirection, issueDate: medicationIssueDate }
        }
        return (
          extraTreatments.find(treatment =>
            [treatment.medication?.productHash, treatment.medication?.variantHash].includes(rowHash)
          ) ?? null
        )
      }

      const approveAs = (
        id: string,
        treatment: { direction: string; issueDate: Date } | null
      ): MedRequestMutationFnProps => ({
        id,
        action: 'approve' as const,
        reason: justification.trim(),
        approveDate: convertDateToISOWithoutConverting(treatment?.issueDate ?? medicationIssueDate),
        extraDirectionsForMedication: treatment?.direction || undefined
      })

      familyActions = mpsApprovalRowsOf(refetchedMedRequest, picksAfterSync).map(row =>
        approveAs(row.id, treatmentOf(row))
      )
    }

    const changeProduct = isMpsSubscription ? undefined : changeProductFor(originalMedRequest, medication)

    // Only the LINK layout offers these checkboxes, and temporary replacement only once the product
    // changed — so a tick left behind after reverting the product change isn't sent.
    const hasLastSubscriptionOrderCheckbox = !isMpsSubscription && isLinkMedRequest(originalMedRequest)
    const hasTemporaryReplacementCheckbox = hasLastSubscriptionOrderCheckbox && !!changeProduct

    // The opened request first, then the rest of the family, one at a time.
    return send(originalMedRequest, {
      actions: [
        {
          id: originalMedRequest.id,
          action: 'approve' as const,
          reason: justification.trim(),
          approveDate: convertDateToISOWithoutConverting(medicationIssueDate),
          additionalNotes: notes,
          extraDirectionsForMedication: medicationDirection,
          changeProduct,
          isTemporaryReplacement: hasTemporaryReplacementCheckbox ? isTemporaryReplacement : undefined,
          // Sent only when ticked; NodeAPI treats an absent flag as false.
          isLastSubscriptionOrder: (hasLastSubscriptionOrderCheckbox && isLastSubscriptionOrder) || undefined,
          scrExecutedPrompt: scrExecutedPrompt,
          scrSummaryResult: scrSummaryPrompt
        },
        ...familyActions
      ],
      isChain: isMpsSubscription,
      isForFuture: isFutureIssueDate(medicationIssueDate)
    })
  }

  // The store flag spans the whole submit (sync + action chain) so the treatments UI can lock
  // card add/remove meanwhile — the payloads were captured at the start, so a mid-flight
  // add or removal would change the page without ever reaching the subscription.
  return async (): Promise<boolean> => {
    const { setApproveSubmitting } = useMedicationRequestStore.getState()
    setApproveSubmitting(true)
    try {
      return await submit()
    } finally {
      setApproveSubmitting(false)
    }
  }
}
